import React, { useState, useRef } from "react";
import { useQuery } from "react-query";
import { DataTable } from "primereact/datatable";
import authApi from "../../../service/IdApi";
import PageLoading from "../../../components/PageLoading";
import PageLoadError from "../../../components/PageLoadError";
import PageHeading from "../../../components/PageHeading";
import { Column } from "primereact/column";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from 'primereact/button';
import { Menu } from "primereact/menu";
import { throttle } from "lodash";
import { useAppContext } from "../../../context/appContext";
import { useNavigate } from "react-router-dom";
import { Dialog } from 'primereact/dialog';
import { Dropdown } from "primereact/dropdown";

const AllProfiles = () => {
    const { displayAlert, switchUnclaimedProfile } = useAppContext();
    const [profiles, setProfiles] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const dataTable = useRef(null);
    const [showVanityUrl, setShowVanityUrl] = useState(false);
    const [showEmail, setShowEmail] = useState(false);
    const [showWebsite, setShowWebsite] = useState(false);
    const [showUpdatedOn, setShowUpdatedOn] = useState(false);
    const [showId, setShowId] = useState(false);
    const [showProviderId, setShowProviderId] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [showWelcomeEmailDialog, setShowWelcomeEmailDialog] = useState(false);
    const [welcomeUnclaimedEmailSent, setWelcomeUnclaimedEmailSent] = useState(false);
    const [emailsSent, setEmailsSent] = useState([]);
    const [totalEmailsSent, setTotalEmailsSent] = useState(0);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const navigate = useNavigate();
    const [rowActions, setRowActions] = useState([]);
    const [publishedFilter, setPublishedFilter] = useState("");
    const [spamFilter, setSpamFilter] = useState("");
    const [verifiedFilter, setVerifiedFilter] = useState("");
    const [featuredFilter, setFeaturedFilter] = useState("");
    const [unclaimedFilter, setUnclaimedFilter] = useState("");
    const [proMemberFilter, setProMemberFilter] = useState("");

    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [users, setUsers] = useState([]);
    const menu = useRef(null);
    const [lazyState, setlazyState] = useState({
        first: 0,
        rows: 50,
        page: 1,
        sortField: "createdAt",
        sortOrder: 0,
    });

    let params = "?first=" + lazyState.first + "&rows=" + lazyState.rows;
    if (lazyState.sortField) {
        params += "&sortField=" + lazyState.sortField;
        if (lazyState.sortOrder) {
            params += "&sortOrder=" + lazyState.sortOrder;
        }
    }

    const { isLoading, isFetching, isError, data, refetch } = useQuery(["Profiles", params], async () => {
        var searchBody = {};
        searchBody.first = lazyState.first;
        searchBody.rows = lazyState.rows;
        searchBody.sortField = lazyState.sortField;
        searchBody.sortOrder = lazyState.sortOrder;
        if (publishedFilter !== "") {
            if (publishedFilter === "published") {
                searchBody.published = true;
            }
            else {
                searchBody.published = false;
            }
        }
        if (unclaimedFilter !== "") {
            if (unclaimedFilter === "claimed") {
                searchBody.unclaimed = false;
            }
            else {
                searchBody.unclaimed = true;
            }
        }
        if (spamFilter !== "") {
            if (spamFilter === "spam") {
                searchBody.untrusted = true;
            }
            else {
                searchBody.untrusted = false;
            }
        }
        if (verifiedFilter !== "") {
            if (verifiedFilter === "verified") {
                searchBody.verified = true;
            }
            else {
                searchBody.verified = false;
            }
        }
        if (featuredFilter !== "") {
            if (featuredFilter === "featured") {
                searchBody.spotlight = true;
            }
            else {
                searchBody.spotlight = false;
            }
        }
        if (proMemberFilter !== "") {
            searchBody.pro = Number(proMemberFilter);
        }
        const result = await authApi.post("admin/profile/list", searchBody);
        setProfiles(result.data.profiles);
        setTotalRecords(result.data.totalRecords);
        setLoaded(true);
        return data;
    });

    const openNew = () => {
        window.location.href = process.env.REACT_APP_NETWORK_SITE + "/company/create-profile";
    };

    const pageHeader = (
        <PageHeading pageTitle="Admin - All Profiles">
            <div className="block">
                <Button label="Create Unclaimed Profile" icon="pi pi-plus" className="mr-2" onClick={openNew} />
            </div>
        </PageHeading>
    );

    if (isLoading) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    if (!loaded) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    const onPage = (event) => {
        setlazyState(event);
    };

    const onSort = (event) => {
        setlazyState(event);
    };

    const exportCSV = (selectionOnly) => {
        dataTable.current.exportCSV({ selectionOnly });
    };

    const confirmWelcomeEmailAction = throttle(async (profile) => {
        setShowWelcomeEmailDialog(true);
        try {
            const response = await authApi.get("admin/profile/email/" + profile.providerId);
            setEmailsSent(response.data.emails);
            setTotalEmailsSent(response.data.totalRecords);
            for (var i = 0; i < response.data.totalRecords; i++) {
                if (response.data.emails[i].type === "new-unclaimed") {
                    setWelcomeUnclaimedEmailSent(true);
                    break;
                }
            }
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }

    }, 500, { trailing: false })

    const hideWelcomeEmailDialog = () => {
        setShowWelcomeEmailDialog(false);
        setWelcomeUnclaimedEmailSent(false);
        setEmailsSent([]);
        setTotalEmailsSent(0);
    }

    const sendWelcomeEmailAction = throttle(async () => {
        setIsSendingEmail(true);
        var body = {};
        body.providerId = selectedProfile.providerId;
        body.type = "new-unclaimed";
        try {
            await authApi.post("admin/profile/email", body);
            setShowWelcomeEmailDialog(false);
            displayAlert("success", "Success", "Email has been sent");
            setIsSendingEmail(false);
            setSelectedProfile(null);
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            setIsSendingEmail(false);
            throw error;
        }
    }, 500, { trailing: false });

    const markAsFeaturedAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/spotlight", body);
            displayAlert("success", "Success", "Profile is now featured");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const unmarkAsFeaturedAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/unspotlight", body);
            displayAlert("success", "Success", "Profile is no longer featured");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const markAsVerifiedAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/verified", body);
            displayAlert("success", "Success", "Profile is now verified");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const unmarkAsVerifiedAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/unverified", body);
            displayAlert("success", "Success", "Profile is no longer verified");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const markAsUntrustedAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/untrusted", body);
            displayAlert("success", "Success", "Profile is now untrusted");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const unmarkAsUntrustedAction = throttle(async (profile) => {
        var body = {};
        body.providerId = profile.providerId;
        try {
            await authApi.post("admin/profile/trusted", body);
            displayAlert("success", "Success", "Profile is no longer untrusted");
            setSelectedProfile(null);
            refetch();
        } catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            throw error;
        }
    }, 500, { trailing: false });

    const showActionMenu = (e, rowData) => {
        const sendWelcomeEmail = {
            label: "Send Welcome Email",
            icon: "pi pi-envelope",
            command: () => {
                confirmWelcomeEmailAction(rowData);
            },
        };
        const markAsFeatured = {
            label: "Feature this profile",
            icon: "pi pi-bolt",
            command: () => {
                markAsFeaturedAction(rowData);
            },
        };
        const unmarkAsFeatured = {
            label: "Remove as featured profile",
            icon: "pi pi-times",
            command: () => {
                unmarkAsFeaturedAction(rowData);
            },
        };
        const markAsVerified = {
            label: "Mark as verified",
            icon: "pi pi-thumbs-up",
            command: () => {
                markAsVerifiedAction(rowData);
            },
        };
        const unmarkAsVerified = {
            label: "Mark as unverified",
            icon: "pi pi-thumbs-down",
            command: () => {
                unmarkAsVerifiedAction(rowData);
            },
        };
        const markAsTrusted = {
            label: "Mark as spam and hide this profile",
            icon: "pi pi-eye-slash",
            command: () => {
                markAsUntrustedAction(rowData);
            },
        };
        const unmarkAsTrusted = {
            label: "Unmark as spam and show this profile",
            icon: "pi pi-eye",
            command: () => {
                unmarkAsUntrustedAction(rowData);
            },
        };

        var _rowActions = []
        if (rowData.unclaimed && rowData.published) {
            _rowActions.push(sendWelcomeEmail);
        }
        if (!rowData.spotlight) {
            _rowActions.push(markAsFeatured);
        }
        else {
            _rowActions.push(unmarkAsFeatured);
        }
        if (!rowData.verified) {
            _rowActions.push(markAsVerified);
        }
        else {
            _rowActions.push(unmarkAsVerified);
        }
        if (!rowData.untrusted) {
            _rowActions.push(markAsTrusted);
        }
        else {
            _rowActions.push(unmarkAsTrusted);
        }
        setRowActions(_rowActions);
        setSelectedProfile(rowData);
        menu.current.toggle(e);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    icon="pi pi-bars"
                    className="p-button-rounded p-button-icon-only p-button-text text-800"
                    onClick={(e) => showActionMenu(e, rowData)}
                    aria-controls="popup_menu"
                    aria-haspopup
                    tooltip="Profile Actions"
                    tooltipOptions={{ showDelay: 1000 }}
                />
            </div>
        );
    };

    const nameClicked = (e) => {
        e.preventDefault();
        switchUnclaimedProfile(selectedProfile.name, selectedProfile.providerId, selectedProfile.vanityName);
    }

    const rowClicked = async (e, rowData) => {
        setSelectedProfile(rowData);
        var body = {};
        body.providerId = rowData.providerId;
        const result = await authApi.post("admin/provider/users", body);
        setUsers(result.data.users);
        setShowDetailsDialog(true);
    }

    const hideDetailsDialog = () => {
        setShowDetailsDialog(false);
    }

    const nameBodyTemplate = (rowData) => {
        return (
            <div className="">
                <a href="#" onClick={(e) => rowClicked(e, rowData)}>
                    {rowData.name}
                </a>
                {/* {!rowData.published && rowData.unclaimed && (
                    <a href="#" onClick={(e) => nameClicked(e, rowData)}>{rowData.name}<span className="ml-2 pi pi-arrow-right-arrow-left" style={{ fontSize: '.8rem' }} /></a>
                )}
                {!rowData.published && !rowData.unclaimed && (
                    <a href="#" onClick={(e) => nameClicked(e, rowData)}>{rowData.name}<span className="ml-2 pi pi-arrow-right-arrow-left" style={{ fontSize: '.8rem' }} /></a>
                )}
                {rowData.published && (
                    <a href="#" onClick={(e) => nameClicked(e, rowData)}>{rowData.name}<span className="ml-2 pi pi-arrow-right-arrow-left" style={{ fontSize: '.8rem' }} /></a>
                )} */}
            </div>
        );
    };

    const unclaimedBodyTemplate = (rowData) => {
        return (
            <div>
                {rowData.unclaimed && (
                    <span>No</span>
                )}
                {!rowData.unclaimed && (
                    <span>Yes</span>
                )}
            </div>
        );
    };

    const publishedBodyTemplate = (rowData) => {
        return (
            <div>
                {rowData.published && (
                    <span>Yes</span>
                )}
                {!rowData.published && (
                    <span>No</span>
                )}
            </div>
        );
    };

    const untrustedBodyTemplate = (rowData) => {
        return (
            <div>
                {rowData.untrusted && (
                    <span>Yes</span>
                )}
                {!rowData.untrusted && (
                    <span>No</span>
                )}
            </div>
        );
    };

    const verifiedBodyTemplate = (rowData) => {
        return (
            <div>
                {rowData.verified && (
                    <span>Yes</span>
                )}
                {!rowData.verified && (
                    <span>No</span>
                )}
            </div>
        );
    };

    const spotlightBodyTemplate = (rowData) => {
        return (
            <div>
                {rowData.spotlight && (
                    <span>Yes</span>
                )}
                {!rowData.spotlight && (
                    <span>No</span>
                )}
            </div>
        );
    };
    const proBodyTemplate = (rowData) => {
        return (
            <>
                <span>
                    {rowData.pro === 0 ? 'No' : rowData.pro === 1 ? 'Free Trial' : rowData.pro === 2 ? 'Active Pro' : ''}
                </span>
            </>
        );
    };

    const handleSearch = () => {
        refetch();
    };

    const emailBodyTemplate = (rowData) => {
        return (
            <div>
                <a href={`/account/admin/user-events?email=${rowData.email}`}>
                    {rowData.email}
                </a>
            </div>
        );
    }

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex py-3 w-full">
                        <div className="flex flex-row align-items-center">
                            <div className="flex mr-3 font-bold">
                                Show/Hide Columns
                            </div>
                            <div className="flex mr-3 align-items-center">
                                <span className="mr-1">Vanity URL</span>
                                <InputSwitch checked={showVanityUrl} onChange={(e) => setShowVanityUrl(e.value)} />
                            </div>
                            <div className="flex mr-3 align-items-center">
                                <span className="mr-1">Email</span>
                                <InputSwitch checked={showEmail} onChange={(e) => setShowEmail(e.value)} />
                            </div>
                            <div className="flex mr-3 align-items-center">
                                <span className="mr-1">Website</span>
                                <InputSwitch checked={showWebsite} onChange={(e) => setShowWebsite(e.value)} />
                            </div>
                            <div className="flex mr-3 align-items-center">
                                <span className="mr-1">Updated On</span>
                                <InputSwitch checked={showUpdatedOn} onChange={(e) => setShowUpdatedOn(e.value)} />
                            </div>
                            <div className="flex mr-3 align-items-center">
                                <span className="mr-1">ID</span>
                                <InputSwitch checked={showId} onChange={(e) => setShowId(e.value)} />
                            </div>
                            <div className="flex align-items-center">
                                <span className="mr-1">Provider ID</span>
                                <InputSwitch checked={showProviderId} onChange={(e) => setShowProviderId(e.value)} />
                            </div>
                        </div>
                        <div className="flex flex-row flex-grow-1 justify-content-end">
                            <Button type="button" icon="pi pi-file-export" onClick={() => exportCSV(false)} data-pr-tooltip="CSV" label="Export" />
                        </div>
                    </div>
                    <div className="flex pb-3 w-full">
                        <div className="flex flex-row align-items-center">
                            <div className="font-bold mr-3 white-space-nowrap">
                                Filter By
                            </div>
                            <div className="p-inputgroup mr-3">
                                <Dropdown
                                    value={publishedFilter}
                                    onChange={(e) => setPublishedFilter(e.value)}
                                    options={[
                                        { label: 'Published', value: 'published' },
                                        { label: 'Unpublished', value: 'unpublished' },
                                        { label: 'Either', value: '' }
                                    ]}
                                    placeholder="Published Status"
                                />
                            </div>
                            <div className="p-inputgroup mr-3">
                                <Dropdown
                                    value={unclaimedFilter}
                                    onChange={(e) => setUnclaimedFilter(e.value)}
                                    options={[
                                        { label: 'Claimed', value: 'claimed' },
                                        { label: 'Unclaimed', value: 'unclaimed' },
                                        { label: 'Either', value: '' },
                                    ]}
                                    placeholder="Claimed Status"
                                />
                            </div>
                            <div className="p-inputgroup mr-3">
                                <Dropdown
                                    value={spamFilter}
                                    onChange={(e) => setSpamFilter(e.value)}
                                    options={[
                                        { label: 'Spam', value: 'spam' },
                                        { label: 'Not Spam', value: 'not spam' },
                                        { label: 'Either', value: '' },
                                    ]}
                                    placeholder="Spam Status"
                                />
                            </div>
                            <div className="p-inputgroup mr-3">
                                <Dropdown
                                    value={verifiedFilter}
                                    onChange={(e) => setVerifiedFilter(e.value)}
                                    options={[
                                        { label: 'Verified', value: 'verified' },
                                        { label: 'Not Verified', value: 'not verified' },
                                        { label: 'Either', value: '' },
                                    ]}
                                    placeholder="Verified Status"
                                />
                            </div>
                            <div className="p-inputgroup mr-3">
                                <Dropdown
                                    value={featuredFilter}
                                    onChange={(e) => setFeaturedFilter(e.value)}
                                    options={[
                                        { label: 'Featured', value: 'featured' },
                                        { label: 'Not Featured', value: 'not featured' },
                                        { label: 'Either', value: '' },
                                    ]}
                                    placeholder="Featured Status"
                                />
                            </div>
                            <div className="p-inputgroup mr-3">
                                <Dropdown
                                    value={proMemberFilter}
                                    onChange={(e) => setProMemberFilter(e.value)}
                                    options={[
                                        { label: 'Free Trial Pro', value: '1' },
                                        { label: 'Active Pro Member', value: '2' },
                                        { label: 'Not Pro Member', value: '0' },
                                        { label: 'Either', value: '' },
                                    ]}
                                    placeholder="Pro Member Status"
                                />
                            </div>
                            <div className="p-inputgroup mr-3">
                                <Button
                                    icon="pi pi-search"
                                    className="p-button-primary"
                                    onClick={handleSearch}
                                    tooltip="Search"
                                    loading={isLoading || isFetching}
                                    label="Search"
                                />
                            </div>
                        </div>
                        <div className="flex flex-row flex-grow-1 justify-content-end">
                        </div>
                    </div>
                    <Menu model={rowActions} popup ref={menu} className="white-space-nowrap" style={{ width: "325px" }} />
                    <DataTable
                        ref={dataTable}
                        value={profiles}
                        // selection={selectedItems}
                        dataKey="id"
                        paginator
                        rows={lazyState.rows}
                        rowsPerPageOptions={[10, 25, 50, 100, 500]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} profiles"
                        emptyMessage="No profiles found."
                        // onRowSelect={onRowSelect}
                        lazy
                        first={lazyState.first}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={lazyState.sortField}
                        sortOrder={lazyState.sortOrder}
                        loading={isLoading || !loaded}
                        selection={selectedProfile}
                        onSelectionChange={(e) => setSelectedProfile(e.value)}
                        selectionMode="single"
                    >
                        <Column body={actionBodyTemplate} headerStyle={{ width: "1%", minWidth: "1rem" }}></Column>
                        <Column body={nameBodyTemplate} field="name" header="Name" sortable />
                        {showVanityUrl && (
                            <Column field="vanityName" header="Vanity URL" sortable />
                        )}
                        {showEmail && (
                            <Column field="email" header="Email" sortable />
                        )}
                        {showWebsite && (
                            <Column field="website" header="Website" sortable />
                        )}
                        <Column body={publishedBodyTemplate} field="published" header="Published" sortable />
                        <Column body={unclaimedBodyTemplate} field="unclaimed" header="Claimed" sortable />
                        <Column body={untrustedBodyTemplate} field="untrusted" header="Spam" sortable />
                        <Column body={verifiedBodyTemplate} field="verified" header="Verified" sortable />
                        <Column body={spotlightBodyTemplate} field="spotlight" header="Featured" sortable />
                        <Column body={proBodyTemplate} field="pro" header="Pro Member" sortable />
                        <Column field="systemRating" header="System Rating" sortable />
                        <Column field="createdAt" header="Created On" sortable />
                        {showUpdatedOn && (
                            <Column field="updatedAt" header="Updated On" sortable />
                        )}
                        {showId && (
                            <Column field="id" header="ID" sortable />
                        )}
                        {showProviderId && (
                            <Column field="providerId" header="Provider ID" sortable />
                        )}
                    </DataTable>
                </div>
            </div>
            <div>
                <Dialog
                    visible={showWelcomeEmailDialog}
                    style={{ width: '50vw' }}
                    breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                    header="Send Unclaimed Profile Welcome Email"
                    modal
                    onHide={hideWelcomeEmailDialog}
                    className="p-fluid"
                >
                    <div className="flex">
                        <>
                            {welcomeUnclaimedEmailSent && (
                                <div className="">
                                    The welcome email was already sent. You can not send it again. Instead, you should nudge them with the other email action.
                                </div>
                            )}
                            {!welcomeUnclaimedEmailSent && selectedProfile && (
                                <div className="flex flex-wrap flex-column">
                                    <div className="flex mb-1">
                                        To send this unclaimed profile a welcome message encouraging them to claim their profile, please click the link below.
                                    </div>
                                    <div className="flex flex-shrink-0">
                                        <Button onClick={() => sendWelcomeEmailAction()} className="p-button-text" label={"Send welcome email to " + selectedProfile.email} loading={isSendingEmail} />
                                    </div>
                                </div>
                            )}
                        </>
                    </div>
                    <div>
                        <hr />
                        <div className="font-bold text-lg mt-3">
                            Previous Emails Sent ({totalEmailsSent})
                        </div>
                        <div>
                            {totalEmailsSent === 0 && (
                                <div>No emails were sent</div>
                            )}
                        </div>
                        <div>
                            {emailsSent.map((email, index) =>
                                <div key={index} className=" mt-2">
                                    <div>
                                        From: {email.sender}
                                    </div>
                                    <div>
                                        To: {email.recipient}
                                    </div>
                                    <div>
                                        Date: {email.sentAt}
                                    </div>
                                    <div>
                                        Type: {email.type}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Dialog>
                <Dialog
                    visible={showDetailsDialog}
                    style={{ width: '50vw' }}
                    breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                    header="Profile Details"
                    modal
                    onHide={hideDetailsDialog}
                    className="p-fluid"
                >
                    {selectedProfile && (
                        <div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Name</label>
                                <div className="col">
                                    <a href="#" onClick={(e) => nameClicked(e)}>
                                        {selectedProfile.name}<span className="ml-2 pi pi-arrow-right-arrow-left" style={{ fontSize: '.8rem' }} />
                                    </a>
                                </div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Provider ID</label>
                                <div className="col">{selectedProfile.providerId}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Profile URL</label>
                                {selectedProfile.published && (
                                    <div className="col">
                                        <a href={process.env.REACT_APP_NETWORK_SITE + "/company/" + selectedProfile.vanityName} target="_blank">
                                            /company/{selectedProfile.vanityName}<span className="ml-2 pi pi-external-link" style={{ fontSize: '.8rem' }} />
                                        </a>
                                    </div>
                                )}
                                {!selectedProfile.published && (
                                    <div className="col">
                                        /company/{selectedProfile.vanityName}
                                    </div>
                                )}
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>System Rating</label>
                                <div className="col">{selectedProfile.rating}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Published</label>
                                <div className="col">{selectedProfile.published ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Claimed</label>
                                <div className="col">{selectedProfile.unclaimed ? 'No' : 'Yes'}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Spam</label>
                                <div className="col">{selectedProfile.untrusted ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Verified</label>
                                <div className="col">{selectedProfile.verified ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Featured</label>
                                <div className="col">{selectedProfile.spotlight ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Pro Member</label>
                                <div className="col">{selectedProfile.pro ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Review Count</label>
                                <div className="col">{selectedProfile.reviewCount}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Created On</label>
                                <div className="col">{selectedProfile.createdAt}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Users</label>
                            </div>
                            <DataTable
                                value={users}
                                dataKey="userId"
                                className="datatable-responsive"
                            >
                                <Column field="userId" header="ID" sortable />
                                <Column field="email" body={emailBodyTemplate} header="Email" sortable />
                                <Column field="createdAt" header="Created On" sortable />
                            </DataTable>
                        </div>

                    )}
                </Dialog>
            </div>
        </div>
    );
};

export default AllProfiles;
